import MQTT, { ISubscriptionGrant, MqttClient } from "mqtt";
import Config from "./Config";
import Global from "./Global";
import { ColorEnum, MQTTPushTypeEnum } from "./SuperEnum";
import Tools from "./Tools";
import ad_device_topic_map from "./vo/dbvo/ad_device_topic_map";
import MQTTBaseVO from "./vo/mqtt/MQTTBaseVO";
import EventBus from "./EventBus";
/**
 * MQTT协议客户端
 */
export default class MQTTService {
    private client?: MqttClient;
    /**
     * 已订阅主题
     */
    private subscribeTopics: { [key: string]: ISubscriptionGrant } = {};
    private readonly TAG: string = "MQTTService";
    /**
     * 连接到服务器
     * @param clean 是否在断开连接时清空之前的消息(true清空 false不清空) 相当于不清空的时候是持久连接(默认保存持久化)
     * @returns 
     */
    public async ConnectAsync(clean: boolean = false): Promise<boolean> {
        return await new Promise(async (r) => {
            try {
                //关闭连接
                await this.CloseAsync();

                const serverUrl: string = Config.MQTTServerURL;
                `正在准备连接到MQTT:${serverUrl}`.Log(this.TAG, ColorEnum.Info);
                if (this.client == null) {
                    const jwtToken = Global.JwtToken.value;
                    const uid = Global.UserInfo().uid;

                    this.client = MQTT.connect(Config.MQTTServerURL, {
                        reconnectPeriod: Config.MQTTReconnectPeriod,
                        clean: clean,
                        connectTimeout: Config.MQTTConnectTimeout,
                        clientId: Tools.IsNull(Global.DeivceNumber) ? uid : Global.DeivceNumber,
                        username: uid,
                        password: jwtToken
                    });

                    this.client.on("connect", async (packet: MQTT.IConnackPacket) => {
                        "MEQX连接成功".Log(this.TAG, ColorEnum.Success);
                        r(true);
                    });

                    this.client.on("message", (topic: string, message: Buffer, packet: MQTT.IPublishPacket) => {

                        if (packet.retain) {
                            Tools.ShowLog(`收到一条保存的MQTT消息!`, "MQTTService", ColorEnum.Warning);
                            return;
                        }

                        // `MEQX received message: ${message} from topic: ${topic}`.Log(this.TAG, ColorEnum.Info);
                        //数据类型解码

                        const messageStr: string = message.toString('utf-8');
                        const jobj: MQTTBaseVO = JSON.parse(messageStr);
                        EventBus.emit(MQTTPushTypeEnum[jobj.PushType], jobj);

                        switch (packet.qos) {
                            case 0: //MQTT级别 0
                                break;
                            case 1: //MQTT级别 1
                                break;
                            case 2: //MQTT级别 2
                                break;
                        }
                    });

                    this.client.on("error", async (error) => {

                        //#region 检查并刷新Token

                        if (await Global.ToRefreshTokenAsync()) {
                            if (this.client) {
                                this.client.options.password = Global.JwtToken.value;
                            }
                        }
                        else {
                            Tools.AlertError("刷新token认证失败!");
                        }
                        //#endregion

                        if (error instanceof MQTT.ErrorWithReasonCode) {
                            `CODE:${error.code} ERROR_MESSAGE:${error.message}`.Log("MEQX连接失败", ColorEnum.Error);
                        }
                        else {
                            "MEQX连接失败".Log(this.TAG, ColorEnum.Error);
                        }
                    })

                    this.client.on("reconnect", () => {
                        "MEQX重新连接".Log(this.TAG, ColorEnum.Warning);
                    });

                    this.client.on("end", () => {
                        "MEQX流关闭事件END".Log(this.TAG, ColorEnum.Error);
                    })

                    this.client.on("offline", () => {
                        "MEQX离线".Log(this.TAG, ColorEnum.Warning);
                    });

                    this.client.on("close", () => {
                        "MEQX重新关闭".Log(this.TAG, ColorEnum.Error);
                        r(false);
                    });
                }
                else {
                    r(true);
                }
            }
            catch (ex) {

                const errorMessage = (ex instanceof Error) ? ex.message : ex;
                `连接MEQX出错了:${errorMessage}`.Log(this.TAG, ColorEnum.Error);
                return r(false);
            }
        });
    }

    /**
     * 初始化设备应该订阅的主题列表
     */
    public async InitDeviceSubscribeTopicAsync(): Promise<boolean> {
        const resp = await Global.api.deviceApi.GetTopicFromDeviceUniqueIdentifierAsync(Global.DeivceNumber);
        if (resp.CheckIsOk()) {
            if (resp.Data) {
                if (resp.Data.length > 0) {
                    await this.SubscribeAsync(resp.Data!.Select(x => x.topic.topic_route_key!));
                }
                else {
                    Tools.AlertError("当前设备未绑定任何主题!");
                }
            }
            return true;
        }
        return false;
    }

    /**
     * 关闭全部订阅
     */
    public async CloseAllSubscribie(): Promise<boolean> {
        return await this.UnSubscribeAsync(Object.keys(this.subscribeTopics));
    }



    /**
     * 获取全部订阅的主题列表
     * @returns 
     */
    public get AllSubscribes() {
        return this.subscribeTopics;
    }

    /**
     * 订阅主题
     * @param topics 
     */
    public async SubscribeAsync(topics: string[]): Promise<boolean> {

        const _topics = await this.client?.subscribeAsync(topics, { qos: 1 })
        if (_topics) {
            for (let i = 0; i < _topics.length; i++) {
                const t = _topics[i];
                `订阅 ${t.topic} 级别:${t.qos} NL:${t.nl} RAP:${t.rap} RH:${t.rh}`.Log(this.TAG, ColorEnum.Success);
                this.subscribeTopics[t.topic] = t;
            }
            return true;
        }
        return false;
    }

    /**
     * 取消订阅
     */
    public async UnSubscribeAsync(topics: string[]): Promise<boolean> {
        try {
            await this.client?.unsubscribeAsync(topics);
            for (let i = 0; i < topics.length; i++) {
                const t = topics[i];
                `删除订阅 ${t} `.Log(this.TAG, ColorEnum.Success);
                delete this.subscribeTopics[t];
            }
        }
        catch (ex) {
            return false;
        }
        return true;
    }


    /**
     * 发布数据
     * @param topic 
     * @param message 
     * @param opts 
     * @returns 
     */
    public async Publish(topic: string, message: string | Buffer, opts?: MQTT.IClientPublishOptions) {
        return await this.client?.publishAsync(topic, message, opts);
    }

    /**
     * 断开连接
     */
    public async CloseAsync() {
        await this.client?.endAsync();
        this.client = undefined;
    }
}